import { FormCard, FormItem, MultiSelect } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { Form as AntdForm, Switch, Checkbox, Divider } from 'antd'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import { useWatch } from 'antd/lib/form/Form'
import { useGroupedCarers, CarerEmailWithName } from 'hooks/useGroupedOptions'
import { useMemo } from 'react'
import {
  validateNotificationRecipients,
  validateAtLeastOneWarning,
} from './warningsSettingsUtils'

const toEmail = (e: CarerEmailWithName | string) =>
  typeof e === 'object' ? e.email : e

const NotificationSettingsTab = () => {
  const { t } = useTranslation()
  const { groupedCarerOptions } = useGroupedCarers(false, 'email')
  const form = AntdForm.useFormInstance()
  const enableNotifications = useWatch('enableNotifications', form) ?? false
  const emailRecipients = useWatch('emailRecipients', form) ?? []
  const emailToName = useMemo(() => {
    const map = new Map<string, string>()
    groupedCarerOptions?.forEach((group) =>
      group.options.forEach((opt) =>
        map.set(String(opt.value), opt.label)
      )
    )
    return map
  }, [groupedCarerOptions])

  return (
    <FormCard>
      <FormItem label={<Text>{t('ENABLE_NOTIFICATIONS')}</Text>}>
        <FormItem name="enableNotifications" valuePropName="checked">
          <Switch />
        </FormItem>
      </FormItem>
      {enableNotifications && (
        <>
          <StyledDivider />

          <FormItem
            name="notificationWarningsValidation"
            dependencies={[
              'notYetClockedInNotification',
              'missedNotification',
              'clockInEarlyNotification',
              'clockInLateNotification',
              'clockOutEarlyNotification',
              'clockOutLateNotification',
              'durationMismatchedNotification',
            ]}
            rules={[
              {
                validator: validateAtLeastOneWarning(form, t),
                validateTrigger: 'onSubmit',
              },
            ]}
          >

          <LabelText>{t('SEND_NOTIFICATIONS_WHEN')}</LabelText>
          </FormItem>

            <CheckboxGroup>
            <FormItem
              name="notYetClockedInNotification"
              valuePropName="checked"
              full
            >
              <Checkbox>
                <Text>{t('NOT_YET_CLOCKED_IN')}</Text>{' '}
                <LightText>{t('HIGHLY_RECOMMENDED')}</LightText>
              </Checkbox>
            </FormItem>
            <FormItem name="missedNotification" valuePropName="checked" full>
              <Checkbox>
                <Text>{t('MISSED')}</Text>{' '}
                <LightText>{t('HIGHLY_RECOMMENDED')}</LightText>
              </Checkbox>
            </FormItem>
            <FormItem
              name="clockInEarlyNotification"
              valuePropName="checked"
              full
            >
              <Checkbox>
                <Text>{t('CLOCKED_IN_EARLY')}</Text>
              </Checkbox>
            </FormItem>
            <FormItem
              name="clockInLateNotification"
              valuePropName="checked"
              full
            >
              <Checkbox>
                <Text>{t('CLOCKED_IN_LATE')}</Text>
              </Checkbox>
            </FormItem>
            <FormItem
              name="clockOutEarlyNotification"
              valuePropName="checked"
              full
            >
              <Checkbox>
                <Text>{t('CLOCKED_OUT_EARLY')}</Text>
              </Checkbox>
            </FormItem>
            <FormItem
              name="clockOutLateNotification"
              valuePropName="checked"
              full
            >
              <Checkbox>
                <Text>{t('CLOCKED_OUT_LATE')}</Text>
              </Checkbox>
            </FormItem>
            <FormItem
              name="durationMismatchedNotification"
              valuePropName="checked"
              full
            >
              <Checkbox>
                <Text>{t('DURATION_MISMATCH')}</Text>
              </Checkbox>
            </FormItem>
          </CheckboxGroup>

          <StyledDivider />

          <TeamMembersContainer>
            <LabelText>{t('SELECT_NOTIFICATION_RECIPIENTS') + ':'}</LabelText>
            <FormItem
              name="emailRecipients"
              rules={[
                {
                  validator: validateNotificationRecipients(form, t),
                  validateTrigger: 'onSubmit',
                },
              ]}
            >
              <MultiSelect
                title={t('SELECT_NOTIFICATION_RECIPIENTS')}
                groupedOptions={groupedCarerOptions}
                hasClear
                hasSearch
                hasSelectAll
                values={(emailRecipients ?? []).map(toEmail)}
                placeholder
                maxTagCount={null}
                onChange={(selected: string[]) => {
                  form.setFieldValue(
                    'emailRecipients',
                    selected.map((email) => ({
                      email,
                      name: emailToName.get(email) ?? email,
                    }))
                  )
                }}
              />
            </FormItem>
          </TeamMembersContainer>
        </>
      )}
    </FormCard>
  )
}

const StyledDivider = styled(Divider)`
  margin: 0;
`

const LabelText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const LightText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  > * {
    margin-bottom: 0;
  }
`

const TeamMembersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export default NotificationSettingsTab
